import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebarStore } from '../../store/sidebarStore';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import useAuthStore from '../../store/authStore';
import {
  LayoutDashboard,
  Thermometer,
  Radio,
  Settings,
  PieChart,
  Map,
  Users,
  Bell,
  ChevronDown,
  ChevronRight,
  Building2,
  AlertCircle,
  Mail,
  FileText,
  Shield,
  CreditCard,
  ShieldAlert,
  MessageSquarePlus,
  FileCheck2,
} from 'lucide-react';

const SUPER_ADMIN_EMAILS = ['adam@thermal-systems.co.uk', 'dave@thermal-systems.co.uk'];

const navigation = [
  {
    name: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    isExpandable: true,
    alwaysExpanded: true,
    subItems: [
      { name: 'Plan View', href: '/dashboard/plan', icon: Map }
    ]
  },
  {
    name: 'Edit Sensors',
    icon: Thermometer,
    href: '/sensors'
  },
  { name: 'Thresholds', href: '/alarms', icon: Bell },
  { name: 'Remote Alerts', href: '/remote-alerts', icon: Mail },
  { name: 'Alarm Log', href: '/alarm-log', icon: AlertCircle },
  { name: 'Base Units', href: '/base-units', icon: Radio },
  { name: 'Zones', href: '/zones', icon: Map },
  { name: 'Reports', href: '/analytics', icon: PieChart },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Audit Logs', href: '/audit-logs', icon: FileText },
  { name: 'Access Control', href: '/access-control', icon: Shield },
  { name: 'Settings', href: '/settings', icon: Settings },
  // { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Help', href: '/help', icon: AlertCircle },
  { name: 'Report an Issue', href: '/support', icon: MessageSquarePlus },
];

export default function Sidebar() {
  const location = useLocation();
  const { isOpen, close } = useSidebarStore();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const zustandUser = useAuthStore((state) => state.user);
  const activeEmail = (authUser?.email || zustandUser?.email || '').toLowerCase();
  const isSuperAdmin = SUPER_ADMIN_EMAILS.map(e => e.toLowerCase()).includes(activeEmail);

  const handleLinkClick = () => {
    close();
  };

  const toggleExpand = (name: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev =>
      prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
    );
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden z-20" onClick={close} />}
      <div className={`${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-64 flex flex-col bg-white/80 backdrop-blur-md dark:bg-gray-800 dark:backdrop-blur-none transform transition-transform duration-200 ease-in-out`}>
        <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto min-h-0">
          <nav className="mt-5 flex-1 px-2 space-y-1">
            {isSuperAdmin && (
              <Link
                to="/super-admin"
                onClick={handleLinkClick}
                className={`${
                  location.pathname === '/super-admin'
                    ? 'bg-[var(--brand-color)] text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:ring-2 hover:ring-inset hover:ring-indigo-500 dark:hover:ring-indigo-400'
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 transition-colors duration-150`}
              >
                <ShieldAlert className="mr-3 h-6 w-6 flex-shrink-0" aria-hidden="true" />
                Super Admin
              </Link>
            )}
            {isSuperAdmin && (
              <Link
                to="/validation-docs"
                onClick={handleLinkClick}
                className={`${
                  location.pathname === '/validation-docs'
                    ? 'bg-[var(--brand-color)] text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:ring-2 hover:ring-inset hover:ring-indigo-500 dark:hover:ring-indigo-400'
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 transition-colors duration-150`}
              >
                <FileCheck2 className="mr-3 h-6 w-6 flex-shrink-0" aria-hidden="true" />
                Validation Docs
              </Link>
            )}
            {navigation.map((item) => {
              const isExpanded = expandedItems.includes(item.name);
              const isActive = location.pathname === item.href || (item.subItems && item.subItems.some(subItem => location.pathname === subItem.href));
              const hasSubItems = item.subItems && item.subItems.length > 0;

              return (
                <div key={item.name}>
                  <div className="flex flex-col">
                    <div className="flex">
                      <Link
                        to={item.href}
                        onClick={handleLinkClick}
                        className={`${
                          isActive
                            ? 'bg-[var(--brand-color)] text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:ring-2 hover:ring-inset hover:ring-indigo-500 dark:hover:ring-indigo-400'
                        } group flex-1 flex items-center px-2 py-2 text-sm font-medium rounded-l-md transition-colors duration-150 ${
                          hasSubItems && item.isExpandable && !item.alwaysExpanded ? 'rounded-r-none' : 'rounded-r-md'
                        }`}
                      >
                        <item.icon
                          className="mr-3 h-6 w-6 flex-shrink-0"
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>

                      {hasSubItems && item.isExpandable && !item.alwaysExpanded && (
                        <button
                          onClick={(e) => toggleExpand(item.name, e)}
                          className={`${
                            isActive
                              ? 'bg-[var(--brand-color)] text-white'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:ring-2 hover:ring-inset hover:ring-indigo-500 dark:hover:ring-indigo-400'
                          } px-2 rounded-r-md flex items-center transition-colors duration-150`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>

                    {hasSubItems && (item.alwaysExpanded || isExpanded) && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={handleLinkClick}
                            className={`${
                              location.pathname === subItem.href
                                ? 'bg-[var(--brand-color)] text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:ring-2 hover:ring-inset hover:ring-indigo-500 dark:hover:ring-indigo-400'
                            } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                          >
                            <subItem.icon
                              className="mr-3 h-4 w-4 flex-shrink-0"
                              aria-hidden="true"
                            />
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}